import { formatHex } from "culori";

// With ~19 regions on a 360° circle, hue alone only buys ~19° of separation
// per step — noticeably tight once you're past a dozen categories. Cycling
// through three lightness/chroma "bands" as the hue advances means
// hue-adjacent categories also differ in value and saturation, not just
// hue, which is what actually keeps them apart at a glance.
const PALETTE_BANDS = [
  { l: 52, c: 74 },
  { l: 70, c: 56 },
  { l: 60, c: 38 },
];

/**
 * Generates `count` colors evenly spaced around the HCL hue circle —
 * perceptually more even than spacing RGB/HSL hues, so neighboring
 * categories (which is what "Region" mostly is, spatially) stay
 * distinguishable instead of clustering into near-identical blues or
 * near-identical greens the way naive HSL spacing does.
 */
export function categoricalPalette(count: number, opts: { lightness?: number; chroma?: number; hueOffset?: number } = {}): string[] {
  const offset = opts.hueOffset ?? 0;
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    const hue = (offset + (360 / count) * i) % 360;
    const band = PALETTE_BANDS[i % PALETTE_BANDS.length];
    const lightness = opts.lightness ?? band.l;
    const chroma = opts.chroma ?? band.c;
    colors.push(formatHex({ mode: "lch", h: hue, c: chroma, l: lightness }));
  }
  return colors;
}

/** Builds a stable name -> color map, sorted so the same input always yields the same assignment. */
export function buildCategoricalScale(values: Iterable<string>): Map<string, string> {
  const unique = [...new Set(values)].sort();
  const palette = categoricalPalette(unique.length || 1);
  return new Map(unique.map((value, i) => [value, palette[i]]));
}

function hashHue(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) >>> 0;
  return h % 360;
}

/**
 * Like buildCategoricalScale, but assigns each group (e.g. each Region's own
 * Bezirke) its OWN evenly-spread palette instead of pulling every value from
 * one shared rotation. A region with 9 districts then gets exactly 40°
 * between them; drawing from one global ~130-way rotation instead, that
 * region's own subset is effectively a random sample of the circle and can
 * clump by chance — which is what made same-region districts hard to tell
 * apart. Each group's palette starts at a different hue offset (derived
 * from the group name) so two groups don't happen to start on the same
 * color.
 */
export function buildGroupedCategoricalScale(values: Iterable<string>, groupOf: (value: string) => string | undefined): Map<string, string> {
  const byGroup = new Map<string, string[]>();
  for (const value of new Set(values)) {
    const group = groupOf(value) ?? "";
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group)!.push(value);
  }

  const result = new Map<string, string>();
  for (const [group, members] of byGroup) {
    members.sort();
    const palette = categoricalPalette(members.length, { hueOffset: hashHue(group) });
    members.forEach((member, i) => result.set(member, palette[i]));
  }
  return result;
}

export const OCCUPANCY_COLORS: Record<string, string> = {
  available: "#3f8f5f",
  blocked: "#8a8f9c",
  reserved: "#c9a227",
  technical: "#8a8f9c",
  emergency: "#8a8f9c",
  occupied: "#1e5c8c",
};

export const REGISTRATION_STATUS_COLORS: Record<string, string> = {
  Zusage: "#2f6d43",
  Absage: "#a3312a",
  offen: "#8a8f9c",
  none: "#d8dbe2",
};
